import { useState, useEffect, useRef } from "react";
import { ToolsShell, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Interval Timer — user-configurable sequence of named intervals with
// optional repeat counts. Audio cues via Web Audio API (system sound
// isn't reliable from a page in background tabs, but a Web Audio "beep"
// scheduled at known offsets is the standard approach).
//
// Wakelock is requested while running (where supported) so the screen
// doesn't sleep during a workout.

const DEFAULT = [
  { name: "Work", seconds: 40 },
  { name: "Rest", seconds: 20 },
];

const beep = (freq = 880, dur = 0.15, vol = 0.25) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = freq;
    o.type = "sine";
    o.connect(g);
    g.connect(ctx.destination);
    g.gain.setValueAtTime(0, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol, ctx.currentTime + 0.01);
    g.gain.linearRampToValueAtTime(0, ctx.currentTime + dur);
    o.start();
    o.stop(ctx.currentTime + dur);
    setTimeout(() => ctx.close(), 300);
  } catch {}
};

export default function IntervalTimer() {
  const [intervals, setIntervals] = useState(DEFAULT);
  const [repeatCount, setRepeatCount] = useState(8);
  const [running, setRunning] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [currentRep, setCurrentRep] = useState(1);
  const [remaining, setRemaining] = useState(intervals[0]?.seconds || 0);
  const rafRef = useRef(null);
  const endTimeRef = useRef(0);
  const wakelockRef = useRef(null);

  const startInterval = (idx, rep) => {
    setCurrentIdx(idx);
    setCurrentRep(rep);
    const sec = intervals[idx]?.seconds || 0;
    setRemaining(sec);
    endTimeRef.current = performance.now() + sec * 1000;
  };

  const tick = () => {
    const ms = endTimeRef.current - performance.now();
    if (ms <= 0) {
      // Move to next
      let nextIdx = currentIdx + 1;
      let nextRep = currentRep;
      if (nextIdx >= intervals.length) {
        nextIdx = 0;
        nextRep++;
      }
      if (nextRep > repeatCount) {
        // Done
        beep(1200, 0.3, 0.4);
        setRunning(false);
        setCurrentIdx(0);
        setCurrentRep(1);
        setRemaining(intervals[0]?.seconds || 0);
        return;
      }
      beep(660, 0.2, 0.35);
      startInterval(nextIdx, nextRep);
    } else {
      setRemaining(ms / 1000);
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    if (running) {
      rafRef.current = requestAnimationFrame(tick);
      // Wakelock
      if ("wakeLock" in navigator) {
        navigator.wakeLock.request("screen").then((w) => { wakelockRef.current = w; }).catch(() => {});
      }
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (wakelockRef.current) { wakelockRef.current.release().catch(() => {}); wakelockRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, currentIdx, currentRep, intervals, repeatCount]);

  const start = () => {
    if (intervals.length === 0) return;
    startInterval(0, 1);
    setRunning(true);
    beep(880, 0.15, 0.3);
  };
  const stop = () => { setRunning(false); };
  const reset = () => { setRunning(false); setCurrentIdx(0); setCurrentRep(1); setRemaining(intervals[0]?.seconds || 0); };

  const addInterval = () => setIntervals([...intervals, { name: "New", seconds: 30 }]);
  const removeInterval = (i) => setIntervals(intervals.filter((_, idx) => idx !== i));
  const updateInterval = (i, key, val) => {
    const next = [...intervals];
    next[i] = { ...next[i], [key]: key === "seconds" ? parseInt(val, 10) || 0 : val };
    setIntervals(next);
  };

  const total = intervals.reduce((s, x) => s + x.seconds, 0) * repeatCount;
  const fmt = (sec) => {
    const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };
  const fmtRem = (sec) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    const t = Math.floor((sec - Math.floor(sec)) * 10);
    return `${m}:${s.toString().padStart(2, "0")}.${t}`;
  };

  return (
    <ToolsShell title="Interval Timer">
      {!running ? (
        <>
          <p style={{ fontSize: 14, color: "#C8CADF", marginTop: 0 }}>
            Build a sequence of work/rest intervals. Use for HIIT, Tabata, multi-stage cooking, study sprints.
          </p>

          <div>
            {intervals.map((iv, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                <input
                  value={iv.name}
                  onChange={(e) => updateInterval(i, "name", e.target.value)}
                  placeholder="Name"
                  style={{ flex: 2, background: "#13141F", border: "1px solid #262839", borderRadius: 8, color: "#E8E9F3", fontSize: 14, padding: "10px 12px" }}
                />
                <input
                  value={iv.seconds}
                  onChange={(e) => updateInterval(i, "seconds", e.target.value)}
                  inputMode="numeric"
                  placeholder="sec"
                  style={{ width: 80, background: "#13141F", border: "1px solid #262839", borderRadius: 8, color: "#E8E9F3", fontSize: 14, padding: "10px 12px", textAlign: "center" }}
                />
                <button onClick={() => removeInterval(i)} disabled={intervals.length === 1} style={{ background: "transparent", color: "#7C8099", border: "1px solid #262839", borderRadius: 8, padding: "8px 12px", cursor: intervals.length > 1 ? "pointer" : "not-allowed", fontSize: 16 }}>−</button>
              </div>
            ))}
            <button onClick={addInterval} style={{ background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer", marginTop: 4, fontWeight: 600 }}>
              + Add interval
            </button>
          </div>

          <div style={{ marginTop: 18, display: "flex", gap: 12, alignItems: "center" }}>
            <label style={{ fontSize: 13, color: "#9A9DBE" }}>Repeat:</label>
            <input
              value={repeatCount}
              onChange={(e) => setRepeatCount(parseInt(e.target.value, 10) || 1)}
              inputMode="numeric"
              style={{ width: 80, background: "#13141F", border: "1px solid #262839", borderRadius: 8, color: "#E8E9F3", fontSize: 14, padding: "10px 12px", textAlign: "center" }}
            />
            <div style={{ fontSize: 13, color: "#7C8099" }}>× cycles · total {fmt(total)}</div>
          </div>

          <button onClick={start} disabled={!intervals.length} style={{ ...primaryBtn, width: "100%", marginTop: 20 }}>
            Start
          </button>
        </>
      ) : (
        <div style={{ textAlign: "center", marginTop: 10 }}>
          <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 4 }}>Cycle {currentRep} of {repeatCount}</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: "#E8E9F3", marginBottom: 14 }}>
            {intervals[currentIdx]?.name}
          </div>
          <div style={{ fontSize: 72, fontWeight: 800, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
            {fmtRem(remaining)}
          </div>
          <div style={{ width: "100%", maxWidth: 400, margin: "20px auto 0", height: 8, background: "#1C1E2E", borderRadius: 4, overflow: "hidden" }}>
            <div style={{
              width: `${100 - (remaining / (intervals[currentIdx]?.seconds || 1)) * 100}%`,
              height: "100%",
              background: TOOLS_ACCENT,
              transition: "width 0.1s linear",
            }} />
          </div>
          <div style={{ marginTop: 22, display: "flex", gap: 10, justifyContent: "center" }}>
            <button onClick={stop} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 14, cursor: "pointer" }}>Pause</button>
            <button onClick={reset} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 14, cursor: "pointer" }}>Reset</button>
          </div>
        </div>
      )}
    </ToolsShell>
  );
}
